import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { variableGroupsApi } from '@/api/variableGroups'
import { useTenantKey } from './useTenantKey'
import type { PaginationParams, SaveVariableGroupRequest, UpdateVariableGroupRequest } from '@/types'

function useVariableGroupsKey(projectId: string | undefined) {
  const tenantKey = useTenantKey()
  return (...parts: string[]) => tenantKey('projects', projectId ?? '', 'variable-groups', ...parts)
}

export function useVariableGroups(projectId: string | undefined) {
  const key = useVariableGroupsKey(projectId)
  return useQuery({
    queryKey: key(),
    queryFn: () => variableGroupsApi.getAll(projectId!),
    enabled: !!projectId,
  })
}

export function useVariableGroup(projectId: string | undefined, groupId: string | undefined) {
  const key = useVariableGroupsKey(projectId)
  return useQuery({
    queryKey: key('detail', groupId ?? ''),
    queryFn: () => variableGroupsApi.getById(projectId!, groupId!),
    enabled: !!projectId && !!groupId,
    staleTime: 0,
    refetchOnMount: 'always',
  })
}

export function useVariableGroupUsage(projectId: string | undefined, pagination: PaginationParams) {
  const key = useVariableGroupsKey(projectId)
  return useQuery({
    queryKey: key('usage', String(pagination.skipCount ?? 0), String(pagination.maxResultCount ?? '')),
    queryFn: () => variableGroupsApi.getUsage(projectId!, pagination),
    enabled: !!projectId,
  })
}

export function useCreateVariableGroup(projectId: string | undefined) {
  const queryClient = useQueryClient()
  const key = useVariableGroupsKey(projectId)
  return useMutation({
    mutationFn: (group: SaveVariableGroupRequest) => variableGroupsApi.create(projectId!, group),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: key() }),
  })
}

export function useUpdateVariableGroup(projectId: string | undefined) {
  const queryClient = useQueryClient()
  const key = useVariableGroupsKey(projectId)
  return useMutation({
    mutationFn: ({ groupId, group }: { groupId: string; group: UpdateVariableGroupRequest }) =>
      variableGroupsApi.update(projectId!, groupId, group),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: key() }),
  })
}

export function useDeleteVariableGroup(projectId: string | undefined) {
  const queryClient = useQueryClient()
  const key = useVariableGroupsKey(projectId)
  return useMutation({
    mutationFn: (groupId: string) => variableGroupsApi.delete(projectId!, groupId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: key() }),
  })
}
